/**
 * useUserRelationship — the single shared source of truth behind the UserActions
 * component's TWO INDEPENDENT controls:
 *
 *   • Follow  — toggles the follow graph (GET follow-status on mount when not
 *               seeded; POST /api/v1/user/{id}/follow|unfollow/). Owns
 *               `isFollowing` + `followLoading` + `toggleFollow`.
 *   • Message — get-or-creates a 1:1 DM (POST /api/v1/dm/conversations/) and
 *               opens the bottom-right MiniDmDock popup seeded to that thread
 *               (QA-1262: the small modal messenger is the DEFAULT for every
 *               inline "Message <person>" action — search dropdown, gallery/
 *               profile cards, wheel-view author byline, follower rows — never a
 *               full-page navigation to /messages/{id}; the full inbox stays
 *               reachable via the header Messages icon). Owns `messageLoading` +
 *               `openMessage`.
 *
 * The two paths are deliberately disjoint: `toggleFollow` NEVER touches the DM
 * endpoints and `openMessage` NEVER touches the follow endpoints — following
 * someone must not open a chat, and messaging must not follow (Gordon, 2026-07-20).
 * Both login-gate guests through the existing non-authenticated modal and are
 * inert on your own row (`isSelf`). Reuses backend endpoints only — no new BE.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import API from "../services/request";
import { API_PATH } from "../utils/config";
import { getLoggedInUser, isUserLoggedIn } from "../utils/helpers";
import { openNonAuthenticatedModal } from "../redux/reducers/nonAuthenticatedModal";
import { useCreateConversation, getStartConversationErrorMessage } from "../services/chat";
import { openDock, setActiveConversation } from "../components/ChatDock/dockStore";

const NON_AUTH_ICON = {
  headerIcon: InfoOutlinedIcon,
  headerIconProps: { sx: { color: "#9B5AF8", width: "24px", height: "24px" } },
};

export default function useUserRelationship(
  userId,
  { initialIsFollowing, fetchStatus = true, onFollowChange } = {}
) {
  const dispatch = useDispatch();
  const createConversation = useCreateConversation();

  const loggedInId = getLoggedInUser()?.id;
  const isSelf =
    loggedInId != null && userId != null && String(loggedInId) === String(userId);

  const seeded = typeof initialIsFollowing === "boolean";
  const [isFollowing, setIsFollowing] = useState(seeded ? initialIsFollowing : undefined);
  const [followLoading, setFollowLoading] = useState(false);
  const [followStatusLoading, setFollowStatusLoading] = useState(false);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // BUG-usman-chat-U1: `initialIsFollowing` used to be read ONLY as the
  // useState initial value, so a parent that LEARNS the follow-state changed
  // (AuthorInfo's own Follow button, whose state ViewWheel owns) could hand
  // down a new seed and this hook would ignore it until a remount/reload —
  // leaving Message stuck on "follow them first" (MEASURED web-stg
  // main.aea8ea43.js 2026-08-19). Re-sync on a genuine seed CHANGE only:
  // syncing on every render would let a STALE static seed clobber this hook's
  // own committed `toggleFollow` result at every parent re-render.
  const prevSeedRef = useRef(initialIsFollowing);
  useEffect(() => {
    if (typeof initialIsFollowing !== "boolean") return;
    if (prevSeedRef.current === initialIsFollowing) return;
    prevSeedRef.current = initialIsFollowing;
    setIsFollowing(initialIsFollowing);
  }, [initialIsFollowing]);

  // Resolve follow-state once when we don't already know it. Skipped for self,
  // for guests (they'd hit the login gate before any follow anyway — showing a
  // plain "Follow" is correct), when the parent seeded it, or when disabled.
  useEffect(() => {
    if (isSelf || !fetchStatus || seeded || userId == null) {
      setFollowStatusLoading(false);
      return;
    }
    if (!isUserLoggedIn()) {
      setFollowStatusLoading(false);
      return;
    }
    let cancelled = false;
    setFollowStatusLoading(true);
    (async () => {
      try {
        const res = await API.get(API_PATH.FOLLOW_STATUS(userId));
        if (!cancelled && mounted.current) setIsFollowing(Boolean(res?.data?.is_following));
      } catch (_e) {
        /* leave undefined -> renders as "Follow" */
      } finally {
        if (!cancelled && mounted.current) setFollowStatusLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, isSelf, fetchStatus, seeded]);

  const gateGuest = useCallback(
    (action, title) => {
      if (isUserLoggedIn()) return false;
      dispatch(openNonAuthenticatedModal({ title, action, type: "user", ...NON_AUTH_ICON }));
      return true;
    },
    [dispatch]
  );

  // --- Follow control (follow graph ONLY) ---------------------------------- //
  const toggleFollow = useCallback(async () => {
    if (isSelf || userId == null || followLoading) return;
    if (gateGuest("follow", "Follow User?")) return;
    const next = !isFollowing;
    try {
      setFollowLoading(true);
      const path = isFollowing
        ? API_PATH.UNFOLLOW_USER(userId)
        : API_PATH.FOLLOW_USER(userId);
      const res = await API.post(path);
      if (!mounted.current) return;
      setIsFollowing(next);
      if (res?.data?.message) toast.success(res.data.message);
      if (onFollowChange) onFollowChange(next);
    } catch (err) {
      if (!mounted.current) return;
      toast.error(err?.response?.data?.message || "Failed to update follow status");
    } finally {
      if (mounted.current) setFollowLoading(false);
    }
  }, [isSelf, userId, isFollowing, followLoading, gateGuest, onFollowChange]);

  // Policy is one-way-follow (Gordon 2026-07-20): you can message anyone you
  // follow. So once we KNOW we don't follow them, messaging is blocked — surface
  // that as a gentle "follow first" affordance, never a raw error. While follow
  // state is still unknown we optimistically allow (a stale block is worse UX
  // than a rare post-click nudge). NOTE: this gates STARTING a conversation only
  // — replying inside an existing thread always works regardless of follow.
  //
  // T22.729: a guest is never "blocked" — the Discover card seeds
  // initialIsFollowing:false (wheel.from_followed) for guests too, which used
  // to grey Message out as "follow to message", a dead end for someone who
  // can't follow either. Guests keep Message tappable; openMessage login-gates.
  const followKnownVal = typeof isFollowing === "boolean";
  const canMessage =
    !isUserLoggedIn() || !followKnownVal || isFollowing === true;

  // --- Message control (DM ONLY) ------------------------------------------- //
  const openMessage = useCallback(async () => {
    if (isSelf || userId == null || createConversation.isLoading) return;
    if (gateGuest("message", "Message User?")) return;
    // Pre-click nudge (never a raw error) when we know the one-way gate blocks.
    if (followKnownVal && isFollowing !== true) {
      toast.info("Follow them to message.");
      return;
    }
    try {
      const conversation = await createConversation.mutateAsync(userId);
      if (!mounted.current) return;
      const id = conversation?.id;
      if (id) {
        // QA-1262: DEFAULT to the small modal popup messenger, not the full
        // /messages page. Seed the shared dock store with the get-or-created
        // conversation (the SAME external-writer seam the expanded Thread's
        // Minimize control uses — setActiveConversation + openDock), so the
        // always-mounted MiniDmDock adopts this thread and pops open bottom-
        // right. Pass the COMPLETE conversation row so the dock renders the real
        // peer + brand palette before its own conversations query rehydrates it
        // (mirrors the QA-1021/QA-1029 minimize handoff). The full inbox stays
        // reachable via the header Messages icon (ChatNavEntry/toggleDock).
        setActiveConversation({ id, conversation });
        openDock();
      } else toast.error("Couldn't open the conversation. Please try again.");
    } catch (err) {
      if (!mounted.current) return;
      // ISP-9999: shared with MiniDmDock's compose "+" picker (services/chat.js)
      // so both entry points that can start a new DM show IDENTICAL copy for
      // the identical server gate.
      toast.error(getStartConversationErrorMessage(err, { isFollowing }));
    }
  }, [isSelf, userId, gateGuest, createConversation, followKnownVal, isFollowing]);

  return {
    isSelf,
    isFollowing: Boolean(isFollowing),
    followKnown: followKnownVal,
    canMessage,
    followLoading,
    followStatusLoading,
    messageLoading: createConversation.isLoading,
    toggleFollow,
    openMessage,
  };
}
